import { Card } from '@platform/design-system';
import { usePageContext } from '../../usePageContext.ts';
import { isProductFiltered } from '../performance.ts';
import { dropProps, EditChrome, type GridEditor } from './EditChrome.tsx';
import { definitionOf, isWidgetVisible, widgetTitleOf } from './registry.ts';
import type { DashboardLayout, DashboardWidget, WidgetContext } from './widget-types.ts';
import styles from './DashboardGrid.module.css';

export type DashboardGridProps = {
  layout: DashboardLayout;
  context: WidgetContext;
  financeVisible: boolean;
  editor?: GridEditor | undefined;
};

type CellProps = {
  widget: DashboardWidget;
  context: WidgetContext;
  index: number;
  count: number;
  editor: GridEditor | undefined;
};

const WidgetCell = ({ widget, context, index, count, editor }: CellProps) => {
  const { t } = usePageContext();
  const { framed, Render } = definitionOf(widget);
  const title = widgetTitleOf(widget, t);
  const content = <Render widget={widget} context={context} />;
  return (
    <div
      className={styles['cell']}
      data-size={widget.size}
      data-editing={editor === undefined ? undefined : true}
      {...(editor === undefined ? {} : dropProps(editor, index))}
    >
      {editor === undefined ? null : (
        <EditChrome
          editor={editor}
          id={widget.id}
          title={title}
          first={index === 0}
          last={index === count - 1}
        />
      )}
      {framed ? <Card title={title}>{content}</Card> : content}
    </div>
  );
};

export const DashboardGrid = ({ layout, context, financeVisible, editor }: DashboardGridProps) => {
  const viewer = { financeVisible, productFiltered: isProductFiltered(context.productFilter) };
  const shown = layout.widgets.filter((widget) => isWidgetVisible(widget, viewer));
  return (
    <div className={styles['grid']}>
      {shown.map((widget) => (
        <WidgetCell
          key={widget.id}
          widget={widget}
          context={context}
          index={layout.widgets.indexOf(widget)}
          count={layout.widgets.length}
          editor={editor}
        />
      ))}
    </div>
  );
};
